import { Res } from '../api';
import { DateUtils, Currency } from 'core/utils';

export type PropertyType = 'string' | 'int' | 'decimal' | 'date' | 'boolean' | 'time';

export interface ParseResult {
	value: any;
	errorMsg: string;
}

// Para poder modificarlo en los tests
let DECIMAL_SEPARATOR = Currency.DECIMAL_SEPARATOR;

export function setDecimalSeparator(separator: string) {
	DECIMAL_SEPARATOR = separator;
}

export function getValue(model: any, path: string, allowInvalidPath = false): any {

	// Optimizo el caso mejor de que path sea una propiedad directa del modelo.
	// Podría ser una propiedad directa con el valor undefined (raro porque solemos usar null, pero bueno), en ese
	// caso, no hay problema porque entrará por el camino lento.
	if (model[path] !== undefined)
		return model[path];

	const stack = path.split('.');
	let value = model;

	while (stack.length && value !== undefined) {

		const infoPath = getInfo(stack.shift() as string);

		if (!(infoPath.property in value) && !allowInvalidPath)
			throw Error(`No existe la propiedad ${infoPath.property} al intentar acceder a la ruta ${path}`);

		if (infoPath.isIndexed) {
			const arr = (value[infoPath.property] || []);

			if (!(infoPath.index in arr) && !allowInvalidPath)
				throw Error(`No existe el índice  ${infoPath.index} al intentar acceder a la ruta ${path}`);

			value = arr[infoPath.index];
		}
		else {
			value = value[infoPath.property];
		}
	}

	return value;
}

export function setValue(model: any, path: string, value: any): void {

	let current = model;
	const stack = path.split('.');

	while (stack.length > 1) {
		const currentProperty = stack.shift() as string;
		current = getValue(current, currentProperty, true) || createValue(current, currentProperty);
	}

	updateValue(current, stack.shift() as string, value);
}

function sanitizeDecimalSeparator(value: any) {
	if (typeof value !== 'string')
		return value;

	return value.replace(DECIMAL_SEPARATOR, '.');
}

export function parse(value: any, type: PropertyType, required: boolean): ParseResult {

	const result: ParseResult = {
		value: type === 'string' ? '' : null,
		errorMsg: ''
	};

	// Si no tengo valor, pero no es necesario, devuelvo el resultado actual con null y sin error
	const valueIsEmpty = value == null || value === '' || (value.trim && value.trim() === '');
	if (valueIsEmpty) {
		result.errorMsg = required ? Res.Msg.MSG_VALUE_REQUIRED() : '';
		return result;
	}

	switch (type) {
		case 'int': {
			value = sanitizeDecimalSeparator(value);
			result.value = Number(value);
			if ((!value && value !== 0) || isNaN(result.value) || (result.value | 0) !== result.value)
				result.errorMsg = Res.Msg.MSG_INVALID_FORMAT();
			break;
		}
		case 'decimal': {
			value = sanitizeDecimalSeparator(value);
			result.value = Number(value);
			if ((!value && value !== 0) || isNaN(result.value))
				result.errorMsg = Res.Msg.MSG_INVALID_FORMAT();
			break;
		}
		case 'string': {
			result.value = value === undefined || value === null ? '' : value.toString();
			if (typeof value === 'undefined')
				result.errorMsg = Res.Msg.MSG_INVALID_FORMAT();
			break;
		}
		case 'time': {
			try {
				result.value = DateUtils.parseTime(value);
				if (value === null || isNaN(result.value.getTime()))
					result.errorMsg = Res.Msg.MSG_INVALID_FORMAT();
			}
			catch (error: any) {
				result.errorMsg = error.message;
			}
			break;
		}
		case 'date': {
			try {
				result.value = DateUtils.parseDate(value);
				if (value === null || isNaN(result.value.getTime()))
					result.errorMsg = Res.Msg.MSG_INVALID_FORMAT();
			}
			catch (error: any) {
				result.errorMsg = error.message;
			}
			break;
		}
		case 'boolean': {
			result.value = !!value;
			break;
		}

		default: throw Error(`No se ha podido parsear el valor ${value} porque no se conoce el tipo: ${type}`);
	}

	return result;
}

export type PropertyPath<T> = ((x: T) => any) | string;

export function propertyPathToString(propertyPath: PropertyPath<any>) {

	if (typeof propertyPath === 'string')
		return propertyPath;

	const body = propertyPath.toString();


	// Miro si es un arrow function

	// Tengo que usar [\s\S] (que es whitespace y no whitespace) en lugar de .
	// porque el . no matchea los saltos de línea
	const found = body.match(/\.([\s\S]*?)($|[;$])/);
	if (!found) {
		throw Error(`No es posible obtener el path a partir de la función:\r\n\r\n${body}`);
	}

	return found[1].replace(/\s/g, '');
}

function createValue(model: any, property: string): any {

	const infoPath = getInfo(property);
	const value: any = infoPath.isIndexed ? model[infoPath.property] || [] : {};

	// Actualizo le añado la nueva propiedad a model porque podría no tenerla
	model[infoPath.property] = value;

	if (!infoPath.isIndexed)
		return value;

	value[infoPath.index] = value[infoPath.index] || {};

	return value[infoPath.index];
}

function getInfo(path: string): { property: string, isIndexed: boolean, index: number } {
	// eslint-disable-next-line no-useless-escape
	const result = /^([^\[]+)(\[(\d+)\])?$/.exec(path);
	if (!result) {
		throw Error(`Invalid path to property: ${path}`);
	}
	return {
		property: result[1],
		index: parseInt(result[3], 10),
		isIndexed: !!result[3]
	};
}

function updateValue(model: any, property: string, value: any) {

	const infoPath = getInfo(property);

	if (infoPath.isIndexed) {
		model[infoPath.property] = model[infoPath.property] || [];
		model[infoPath.property][infoPath.index] = value;
	} else {
		model[infoPath.property] = value;
	}
}
